import { useCallback, useEffect, useState, type ReactNode } from "react";

import { money } from "../money.ts";
import {
  arrivalHeadline,
  distanceLabel,
  driverLine,
  etaAbsenceWords,
  finalFareCell,
  positionAgeLabel,
  positionConfidence,
  stageChip,
  stageEvidenceWords,
  tripAbsenceWords,
  type JourneyView,
} from "../ride-journey.ts";
import type { RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { Hero, MapHero } from "./Hero.tsx";
import { Absent, Note, PhoneShell, Rows, type ActionProps } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `6 Driver coming`, `7 In trip`, `8 Complete` and `8b Fare differs` — four screens, one read.
 *
 * EXP-002's `ride-journeys` composes eight services behind them, and the four are the same ride at
 * four moments. What makes them one component is that **the stage decides what is true and the
 * address decides only which furniture is drawn**: the map or the illustration, the driver card or
 * the fare box, the stars or the note. The chip, the title and the sub-line always report what
 * EXP-002 actually said.
 *
 * That split is the whole safety of these four screens. A screen that drew _"Arriving in 4 min"_
 * because the address said `driver-coming` would be drawing a sentence nothing published; one that
 * drew the design's `driver_en_route` pill over `stage: "unknown"` would be the over-claim R-09 and
 * R-18 each record once and R-23 records twice.
 *
 * ## Three of these four are about a trip, and a rider cannot name one
 *
 * **R-28 is the fact this component is shaped around.** Nothing published joins a ride request to
 * the trip created from it: TRP-01's listing is behind `trip:workload:read`, TRP-02's behind
 * `trip-tracking:operations`, DSP-03's offers only `status` and `driverId`, and MOB-03's ride request
 * publishes no trip reference at all. The join exists in exactly one direction and it is the
 * direction a rider cannot traverse.
 *
 * So EXP-002 takes `tripId` from the caller and answers `tripAbsence: not_joinable` when it has
 * none — and then `stage` is never later than `requested`, `driver` is null with
 * `driverAbsence: no_trip`, `position` is null with `positionAbsence: no_trip`, and `eta` is null
 * with `etaAbsence: stage_has_no_leg`. Every one of those is drawn here as the designed absence. The
 * measurement grades these screens *degraded* by granting them a `tripId`; this build does not grant
 * itself one, and what that looks like is on the screen.
 *
 * ## The four absences the measurement names, and where each is drawn
 *
 * | Designed element                   | Drawn as                                              |
 * | ---------------------------------- | ----------------------------------------------------- |
 * | `KDA 417Q`                         | the plate slot, dashed — `vehicleAbsence: not_joinable` |
 * | `Toyota Axio · silver`             | the vehicle line, dashed — same absence                |
 * | `chat`                             | the round control, drawn and unpressable with a reason |
 * | `Your trip is shared with Mama`    | the note, saying sharing cannot be read back           |
 * | `Paid · M-Pesa ··· 0421`           | the row, dashed — no masked instrument is published    |
 * | `Final fare KES 2,290` and its rows | the fare cell and the row, dashed — `finalFareAbsence` |
 * | `Question this fare`               | the button, drawn and unpressable with a reason        |
 */

export type RideScreenId = "driver-coming" | "in-trip" | "complete" | "fare-differs";

export interface RideProps {
  readonly api: RiderApi;
  readonly tenantId: string;
  readonly customerId: string;
  readonly scopes: ReadonlySet<string>;
  readonly rideRequestId: string | undefined;
  readonly tripId: string | undefined;
  readonly screen: RideScreenId;
  readonly onBack: () => void;
  /** Mints a v4 uuid. Injected so a test and the acceptance check get a stable one. */
  readonly newId: () => string;
  readonly now: () => number;
}

export function Ride(props: RideProps): ReactNode {
  const { api, tenantId, customerId, rideRequestId, tripId } = props;

  const [view, setView] = useState<JourneyView | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [placeName, setPlaceName] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [rated, setRated] = useState(false);

  const load = useCallback(() => {
    if (rideRequestId === undefined) return;
    setState({ kind: "loading" });
    api
      .rideJourney({
        tenantId,
        customerId,
        rideRequestId,
        ...(tripId === undefined ? {} : { tripId }),
      })
      .then((answer) => {
        setView(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.journey])));
  }, [api, tenantId, customerId, rideRequestId, tripId]);

  useEffect(load, [load]);

  /*
   * The place the current leg ends, as a name.
   *
   * `eta.to` is the **only** coordinate EXP-002 publishes outside the driver's own position: the
   * `estimate` carries the fare, its expiry and its line items, and no geometry at all. So the
   * pickup on `6 Driver coming` and the destination on `7 In trip` can be named exactly when there
   * is an arrival estimate — and there is one exactly when there is a verified trip, a position and
   * a quote. On a journey with `tripAbsence: not_joinable` there is no coordinate anywhere on the
   * answer, and the place name is an absence rather than a guess. Recorded as a reconciliation
   * finding: the fix is for EXP-002 to carry the quote's origin and destination on `estimate`, which
   * it reads and drops.
   */
  useEffect(() => {
    const to = view?.eta?.to;
    if (to === undefined) {
      setPlaceName(null);
      return;
    }
    let current = true;
    api
      .reverseGeocode({ latitude: to.latitude, longitude: to.longitude, accuracyMeters: 0 })
      .then((answer) => {
        if (current) setPlaceName(answer.address.formatted);
      })
      .catch(() => {
        if (current) setPlaceName(null);
      });
    return (): void => {
      current = false;
    };
  }, [api, view]);

  const raiseSos = useCallback(() => {
    if (view?.tripId === null || view?.tripId === undefined) return;
    void api
      .raiseSafetyIncident({
        tenantId,
        tripId: view.tripId,
        messageId: props.newId(),
        command: { type: "open_sos" },
      })
      .catch(() => undefined);
  }, [api, tenantId, view, props]);

  const shareTrip = useCallback(() => {
    if (view?.tripId === null || view?.tripId === undefined) return;
    void api
      .raiseSafetyIncident({
        tenantId,
        tripId: view.tripId,
        messageId: props.newId(),
        command: { type: "set_sharing", enabled: true },
      })
      .catch(() => undefined);
  }, [api, tenantId, view, props]);

  const rate = useCallback(() => {
    const trip = view?.tripId;
    const driverId = view?.driver?.driverId;
    if (trip === null || trip === undefined || driverId === undefined || score === 0) return;
    void api
      .submitRating(
        {
          // RAT-01 requires the identifier from the caller and publishes no route that allocates
          // one, so the client mints it — which also makes the retry of a dropped submission the
          // same rating rather than a second one.
          ratingId: props.newId(),
          transactionId: trip,
          raterId: customerId,
          subjectId: driverId,
          score,
          submittedAt: new Date(props.now()).toISOString(),
        },
        `rating:${trip}:${customerId}`,
      )
      .then(() => setRated(true))
      .catch(() => undefined);
  }, [api, view, score, customerId, props]);

  if (rideRequestId === undefined) {
    return (
      <StateScreen
        screen="journey"
        chip={{ class: "unknown", label: "no ride" }}
        title="This link names no ride"
        sub="A ride is read by its own identifier and this address carries none. Open it from the booking you made on this phone."
      />
    );
  }

  if (state.kind !== "ready" || view === undefined) {
    return (
      <StateScreen
        screen={shellClass(props.screen)}
        state={state}
        onRetry={load}
        purpose="Looking up your ride."
      />
    );
  }

  const chip = stageChip(view.stage);
  const confidence = positionConfidence(view);
  /*
   * The red SOS, armed where the design arms it.
   *
   * The design's own code arms it on `enroute` and `intrip` and nowhere else
   * (`inTrip = sc==='intrip'||sc==='enroute'`), while its notes say "on every trip screen". The code
   * is a value in the design file and the note is prose about it, so section 1's "copy every value
   * from the design file" settles it: two screens. The wider claim is recorded as a design question
   * rather than built on.
   */
  const trip = props.screen === "driver-coming" || props.screen === "in-trip";
  const safety = {
    armed: trip,
    ...(view.tripId === null
      ? {
          unavailable: `Safety needs the trip this ride became, and ${tripAbsenceWords(view.tripAbsence).toLowerCase()}`,
        }
      : { onPress: raiseSos }),
  };

  const common = {
    back: { glyph: "arrow_back" as const, label: "Back", onPress: props.onBack },
    safety,
    chip,
  };

  const degradedFlag = view.degraded
    ? `some of this ride couldn't be read · ${stageEvidenceWords(view.stageEvidence)}`
    : undefined;

  if (props.screen === "driver-coming" || props.screen === "in-trip") {
    const arrival = arrivalHeadline(view);
    const eta = view.eta;

    const title =
      props.screen === "in-trip"
        ? eta === null
          ? arrival.headline
          : `On the way to ${placeName ?? "your destination"}`
        : arrival.headline;

    const sub =
      eta === null
        ? view.tripId === null
          ? tripAbsenceWords(view.tripAbsence)
          : etaAbsenceWords(view.etaAbsence)
        : props.screen === "in-trip"
          ? `Arrive about ${clockOf(props.now() + eta.durationSeconds * 1000)} · ${distanceLabel(eta.distanceMetres)} left`
          : `Meet at ${placeName ?? "your pickup point"}`;

    const actions: ActionProps[] =
      props.screen === "driver-coming"
        ? [
            {
              label: "Share trip",
              glyph: "ios_share",
              primary: false,
              ...(view.tripId === null || !props.scopes.has(SCOPES.safety)
                ? {
                    unavailable:
                      view.tripId === null
                        ? "Sharing is addressed by the trip, and this app cannot find it. Nothing published joins a booking to the trip made from it."
                        : "Your sign-in doesn't allow this. It needs safety:write.",
                  }
                : { onPress: shareTrip }),
            },
          ]
        : [];

    return (
      <PhoneShell
        screen="journey"
        hero={
          <MapHero
            kind={props.screen === "in-trip" ? "trip" : "approach"}
            vehicle={confidence}
            {...(view.position === null ? {} : { accuracyMetres: view.position.accuracyMetres })}
            {...(confidence === "stale" && view.position !== null
              ? { staleness: `last seen ${positionAgeLabel(view.position)}` }
              : {})}
          />
        }
        {...common}
        title={title}
        sub={sub}
        actions={actions}
        /*
         * The flag line is drawn only for a **degraded** answer, and not for an absence.
         *
         * It is an element the design does not draw, and on a sheet whose children are pinned to the
         * floor an extra 16px line moves everything below it — measured at the stars on
         * `8 Complete`, where the design's four gold stars and this build's stopped overlapping
         * entirely and the comparison went from 2.11% to 2.45% for a line of explanatory text.
         *
         * An absence needs no flag: it is already drawn as the dashed mark, which says what it is.
         * A degraded answer is different — part of this screen could not be read at all — and that is
         * worth one line that is not otherwise anywhere on the screen.
         */
        {...(degradedFlag === undefined ? {} : { flag: degradedFlag })}
      >
        <DriverCard view={view} onShare={shareTrip} canShare={props.scopes.has(SCOPES.safety)} />

        {props.screen === "in-trip" ? (
          <Note
            kind="info"
            /*
             * `Your trip is shared with Mama (live link).`
             *
             * SAF-01 accepts `set_sharing` with `contactAliases`, so a rider **can** share — and
             * nothing publishes the aliases back. There is no `incident.contactAliases`,
             * `sharing.contactAliases` or `trip.sharing` to read, so a screen naming who the trip is
             * shared with would be naming somebody from nothing. The note keeps its place and says
             * which half is missing.
             */
            text="Trip sharing can be switched on, but who it's shared with isn't published back, so this app can't name them."
          />
        ) : null}
      </PhoneShell>
    );
  }

  /* ------------------------------------------------- `8 Complete` and `8b Fare differs` -------- */

  const completed = view.stage === "completed";
  const estimate = view.estimate;
  const final = finalFareCell(view);
  const differs = props.screen === "fare-differs";
  const driver = driverLine(view);

  const canRate =
    view.tripId !== null &&
    view.driver !== null &&
    score > 0 &&
    !rated &&
    props.scopes.has(SCOPES.rate);

  return (
    <PhoneShell
      screen={`journey journey--hero`}
      hero={<Hero kind={completed ? "done" : "none"} />}
      {...common}
      title={
        differs
          ? /*
             * `Final fare KES 2,290`.
             *
             * There is no final fare and there never is one: TRP-01's trip carries no fare, PRC-02's
             * calculation is a `POST` needing a rate card and trip measurements this application does
             * not hold, and nothing publishes a completed trip's charged total as a read. So the
             * headline says which kind of nothing it is, and the designed figure is reported as a
             * measured difference rather than assembled from a rate card — which would be money with
             * no accounting behind it.
             */
            final.value === null
            ? "Your final fare isn't published"
            : `Final fare ${final.value}`
          : completed
            ? "You've arrived"
            : stageHeadline(view)
      }
      /*
       * Both sub-lines are kept to one line at 320dp, which is a layout constraint rather than a
       * stylistic one: the sheet's children are pinned to its floor, so a sentence that wraps to
       * three lines moves the fare box, the rows and the stars down by about thirty pixels and every
       * one of them then differs from the design in full. The first draft of this screen said the
       * same thing in three lines and cost 0.3% of the union for it.
       */
      sub={
        differs
          ? "A finished ride's charge isn't published."
          : /*
             * `JKIA Terminal 1A · 06:16` — the place and the minute the ride ended.
             *
             * Neither is on this answer. EXP-002 publishes no trip timestamps, and the only
             * coordinate it carries outside the driver's position is `eta.to` — which is absent at
             * `completed`, because a finished ride has no leg left to estimate. So the sub-line says
             * so. Same reconciliation finding as the place name above.
             */
            "Where and when it ended isn't published."
      }
      {...(degradedFlag === undefined ? {} : { flag: degradedFlag })}
      actions={[
        differs
          ? {
              label: "Question this fare",
              glyph: "help",
              primary: false,
              unavailable:
                "Opening a case needs POST /internal/v1/tenants/{tenantId}/support-cases, and the gateway does not expose /internal/v1.",
            }
          : {
              label: driver.name === null ? "Rate your driver" : `Rate ${driver.name}`,
              glyph: "star",
              primary: true,
              ...(canRate
                ? { onPress: rate }
                : {
                    unavailable: rated
                      ? "Thanks — your rating has been sent."
                      : view.tripId === null
                        ? "A rating is about a trip, and this app cannot find yours. Nothing published joins a booking to the trip made from it."
                        : view.driver === null
                          ? "There is no driver on this ride to rate."
                          : score === 0
                            ? "Choose a score first."
                            : "Your sign-in doesn't allow rating. It needs rating:submit.",
                  }),
            },
      ]}
    >
      <div className="fare">
        <div className="fare__cell">
          <span className="fare__label">Est.</span>
          {estimate === null ? (
            <Absent words="no estimate published" className="fare__absent" />
          ) : (
            <span className="fare__value">{money(estimate.fare)}</span>
          )}
        </div>
        <div
          className={`fare__cell fare__cell--final${differs ? " fare__cell--differs" : completed ? " fare__cell--done" : ""}`}
        >
          <span className="fare__label">Final</span>
          {final.value === null ? (
            <Absent words={final.because} className="fare__absent" />
          ) : (
            <span className="fare__value">{final.value}</span>
          )}
        </div>
      </div>

      <Rows
        rows={
          differs
            ? [
                {
                  key: "What the extras were",
                  value: null,
                  because: "no fare breakdown published for a finished ride",
                },
              ]
            : [{ key: "Paid", value: null, because: "no stored instrument published" }]
        }
      />

      {differs ? (
        <Note
          kind="info"
          /*
           * `Both lines come from the published fee schedule v12.`
           *
           * The design's sentence is a claim about lines this application does not have, so
           * repeating it would be asserting the provenance of a breakdown nobody published. The note
           * keeps its place and says which half is missing, flagged as this build's words.
           */
          text="When a final fare is published it will name the fee schedule each extra came from. Nothing publishes one today."
        />
      ) : (
        <div className="stars" role="radiogroup" aria-label="Rate your driver">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={value === score}
              aria-label={`${String(value)} of 5`}
              className={`glyph stars__star${value <= score ? " stars__star--on" : ""}`}
              onClick={() => setScore(value)}
            >
              star
            </button>
          ))}
        </div>
      )}
    </PhoneShell>
  );
}

/* ------------------------------------------------------------------------------- the driver card */

/**
 * `P. Njoroge · 4.9`, the plate, the vehicle, and two round controls.
 *
 * Drawn on both trip screens, and three of its five pieces have no source. The card is drawn anyway
 * because the design draws it and nothing may be removed; what changes is that each absent piece says
 * which kind of nothing it is.
 */
function DriverCard({
  view,
  onShare,
  canShare,
}: {
  readonly view: JourneyView;
  readonly onShare: () => void;
  readonly canShare: boolean;
}): ReactNode {
  const driver = driverLine(view);
  const initials = driver.name === null ? "?" : initialsOf(driver.name);

  return (
    <div className="card">
      <span className="card__avatar" aria-hidden="true">
        {initials}
      </span>
      <span className="card__text">
        {driver.name === null ? (
          <Absent words={driverAbsenceLine(view)} />
        ) : (
          <span className="card__name">
            {driver.rating === null ? driver.name : `${driver.name} · ${driver.rating}`}
            {/*
             * A rating RAT-03 itself called stale is not drawn as a number.
             *
             * `ratingConfirmed: false` means the aggregate is behind its own facts. A stale 4.9 is a
             * 4.9 a rider acts on, and the recorded incident of a staleness flag being published and
             * not consulted is R-23 — twice, on the dispatch live map.
             */}
            {driver.ratingIsStale ? <Absent words="rating not current" /> : null}
          </span>
        )}
        {/*
         * `KDA 417Q` and `Toyota Axio · silver`.
         *
         * Nothing on this platform publishes `driverProfile.vehicleId`, `trip.vehicleId`,
         * `assignments.vehicleId`, `availability.vehicleId`, `vehicle.make`, `vehicle.model` or
         * `vehicle.colour`, so there is no path from the driver who is coming to the car they are
         * driving. EXP-002 answers `vehicle: null` with `vehicleAbsence: not_joinable`, and a rider
         * meeting a car at a kerb needs to know the plate is unknown rather than guess at one.
         */}
        <Absent words="plate not published" />
        <Absent words="make and colour not published" />
      </span>

      <button
        type="button"
        className="card__action"
        aria-label="Message your driver"
        aria-disabled
        title="Nothing publishes a message thread with a driver: there is no thread.messages, messages.body or conversation.messages anywhere on this platform."
      >
        <span className="glyph" aria-hidden="true">
          chat
        </span>
      </button>

      <button
        type="button"
        className="card__action"
        aria-label="Share your trip"
        {...(view.tripId === null || !canShare
          ? {
              "aria-disabled": true,
              title:
                view.tripId === null
                  ? "Sharing is addressed by the trip, and this app cannot find it."
                  : "Your sign-in doesn't allow this. It needs safety:write.",
            }
          : { onClick: onShare })}
      >
        <span className="glyph" aria-hidden="true">
          share_location
        </span>
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------------------------- the helpers */

/** Which stylesheet modifier a screen's state view borrows, so the hero keeps its designed height. */
function shellClass(screen: RideScreenId): string {
  return screen === "complete" || screen === "fare-differs" ? "journey journey--hero" : "journey";
}

/** Two letters from a name, which is what the design's 42dp avatar holds. */
function initialsOf(name: string): string {
  const words = name.split(/[\s.]+/).filter((word) => /^[A-Za-z]/.test(word));
  if (words.length === 0) return name.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words.at(-1)![0]}`.toUpperCase();
}

/** `06:18`, on this device's clock, which is the only clock there is. */
function clockOf(at: number): string {
  const when = new Date(at);
  return `${String(when.getHours()).padStart(2, "0")}:${String(when.getMinutes()).padStart(2, "0")}`;
}

/**
 * The headline on `8 Complete` when the ride is not, in fact, complete.
 *
 * Reachable whenever a rider opens a shared link to a ride that is still running, or whose stage
 * EXP-002 could not confirm. `You've arrived` over `stage: "unknown"` is the exact wrong answer this
 * whole component is arranged to avoid.
 */
function stageHeadline(view: JourneyView): string {
  switch (view.stage) {
    case "cancelled":
      return "This ride was cancelled";
    case "no_show":
      return "Nobody was picked up";
    case "unknown":
      return "We can't tell how this ride ended";
    default:
      return "This ride hasn't ended yet";
  }
}

/** Why there is no driver's name, in the card's own small type. */
function driverAbsenceLine(view: JourneyView): string {
  if (view.driver === null) return "no driver named";
  return "name not published";
}
